import { mat4, quat, vec2, vec3 } from 'gl-matrix';
import { gl } from '../../../Engine';
import { Renderer } from '../../../core/render/Renderer';
import { FrameBufferObject } from '../../../core/utils/FrameBufferObject';
import { ShaderProgram } from '../../../core/utils/ShaderProgram';
import { UniformBoolean } from '../../../core/utils/uniforms/UniformBoolean';
import { ContourTexture } from '../../../data/structure/ContourTexture';
import { RegionOfInterest } from '../../../data/structure/RegionOfInterest';
import { BooleanOperation } from './SDFOperations';

export enum SDFOperation {
    ADD,
    SUBTRACT,
}

export class SDFOperationRenderer implements Renderer {
    private readonly booleanShader: BooleanShader;

    public constructor() {
        this.booleanShader = new BooleanShader();
    }

    /**
     * Renders the {@link sources} onto the {@link destination} using the specified {@link operation}.
     * @param destination {@link RegionOfInterest} or {@link ContourTexture} on which the {@link sources} should be rendered.\
     * Destination should already cover the volume of the sources.
     * @param operation {@link BooleanOperation} or {@link SDFOperation} that will be used for rendering.
     * @param sources {@link Array} of {@link RegionOfInterest RegionsOfInterest} or {@link ContourTexture ContourTextures} that will be rendered onto the {@link destination}.
     */
    public renderOnDestination(
        destination: RegionOfInterest | ContourTexture,
        operation: BooleanOperation | SDFOperation,
        ...sources: (RegionOfInterest | ContourTexture)[]
    ) {
        let destTexture: ContourTexture | undefined = undefined;
        if (destination instanceof RegionOfInterest) {
            if (!(destTexture = destination.texture)) {
                throw new Error(
                    `Attempt to render ${sources.length} onto ROI ${destination.roiNumber}. ${destination.name}, but that ROI does not have ContourTexture`
                );
            }
        } else {
            destTexture = destination;
        }
        const sdf = destTexture.signedDistanceField;

        if (!sdf) {
            throw new Error(
                `Attempt to render ${sources.length} ROIs or ContourTextures onto ROI with RoiNumber`
            );
        }

        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE);
        let inverse = false;
        switch (operation) {
            case BooleanOperation.AND_NOT:
                inverse = true;
                gl.blendEquation(gl.MIN);
                break;
            case BooleanOperation.AND:
                gl.blendEquation(gl.MIN);
                break;
            case BooleanOperation.OR:
                gl.blendEquation(gl.MAX);
                break;
            case SDFOperation.ADD:
                gl.blendEquation(gl.FUNC_ADD);
                break;
            case SDFOperation.SUBTRACT:
                gl.blendEquation(gl.FUNC_SUBTRACT);
                break;
        }

        const fbo = new FrameBufferObject(
            vec2.fromValues(sdf.size[0], sdf.size[1])
        );
        //TODO: Should consider registrations
        //rotation from ImageSpace to PatientSpace
        const rotation = destTexture.rotation;

        //center in ImageSpace, Z set based on slice index
        const destCenter = vec3.clone(destTexture.contentBoundingBox.center);
        //center in PatientSpace, derived from destCenter
        const center = vec3.create();

        const numberOfSlices = destTexture.textureGrid.sizeZ;
        /**
         * POI in texture space
         */
        gl.viewport(0, 0, sdf.size[0], sdf.size[1]);
        this.booleanShader.start();
        this.booleanShader.inverse.loadBoolean(inverse);
        const scale = vec3.scale(
            vec3.create(),
            destTexture.fullBoundingBox.size,
            0.5
        );

        scale[2] = 1.0;

        const imageToPatient = mat4.create();
        const patientToImage = mat4.create();

        const q = quat.fromMat3(quat.create(), rotation);

        for (let k = 0; k < numberOfSlices; ++k) {
            fbo.createTextureLayer(sdf.texture, k);
            destCenter[2] = destTexture.sliceIndexToNormalPosition(k);
            vec3.transformMat3(center, destCenter, rotation);
            mat4.fromRotationTranslationScale(imageToPatient, q, center, scale);
            mat4.invert(patientToImage, imageToPatient);
            for (const source of sources) {
                let srcTexture: ContourTexture;
                if (source instanceof RegionOfInterest) {
                    if (source.texture) {
                        srcTexture = source.texture;
                    } else {
                        continue;
                    }
                } else {
                    srcTexture = source;
                }
                if (!srcTexture.signedDistanceField) {
                    continue;
                }
                const vao = srcTexture.crossSection(patientToImage);
                if (vao) {
                    vao.bind(0, 1);
                    srcTexture.bindTexture();
                    gl.drawElements(
                        gl.TRIANGLES,
                        vao.indexCount,
                        gl.UNSIGNED_INT,
                        0
                    );
                    vao.unbind(0, 1);
                    vao.delete();
                }
            }
        }
        this.booleanShader.stop();

        fbo.unbind();
        fbo.delete();
        gl.disable(gl.BLEND);
    }

    public delete(): boolean {
        this.booleanShader.delete();
        return true;
    }
}

class BooleanShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es
    
    precision highp float;
    
    in vec2 position;
    in vec3 textureCoords;
    
    out vec3 pass_textureCoords;
    
    void main(){
        gl_Position = vec4(position, 0.0, 1.0);
        pass_textureCoords = textureCoords;
    }`;

    private static readonly FRAGMENT_CONTENT = `#version 300 es

    precision highp float;
    precision highp sampler3D;

    in vec3 pass_textureCoords;

    out vec4 outColor;

    uniform bool inverse;
    uniform sampler3D sampler;

    void main(){
        outColor = texture(sampler, pass_textureCoords);
        if(inverse){
            outColor.rgb = vec3(1.0) - outColor.rgb;
        }
        outColor.a = 1.0;
    }
    `;

    public readonly inverse: UniformBoolean;

    public constructor() {
        super(
            BooleanShader.VERTEX_CONTENT,
            BooleanShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );
        this.inverse = new UniformBoolean('inverse');
        super.storeUniformLocations(this.inverse);
    }
}
